<script>
import { declineProspect } from '@src/api/personnelManagement'
import FormItem from '@components/form-item.vue'
import { isMobileMixin } from '@src/mixins/isMobileMixin'
import SimpleDivider from '@components/simple-divider.vue'
import ProfilePicture from '@components/profile-picture.vue'
import InfoBlock from '@components/info-block.vue'
import moment from 'moment'
import { arrayToSentence } from '@utils/string-helpers'
import { authComputed, personnelComputed } from '@state/helpers'

export default {
  components: { InfoBlock, ProfilePicture, SimpleDivider, FormItem },
  mixins: [isMobileMixin],
  props: {
    prospect: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      modalButtonLoading: false,
      preventClosing: true,
      reasonForProspectDecline: '',
      reasonForProspectDeclineOther: '',
      reasonsForProspectDecline: ['Not Actively Hiring', 'Not Enough Experience', 'Poor Interview', 'Other'],
      delayToEnableClosing: 5000,
    }
  },
  computed: {
    ...personnelComputed,
    ...authComputed,
    isReasonForDeclineValid() {
      if (this.reasonForProspectDecline === 'Other') {
        return this.reasonForProspectDeclineOther.length > 4 && this.reasonForProspectDeclineOther.length <= 200
      }

      return !!this.reasonForProspectDecline
    },
    assignedTo() {
      return this.prospect.assignedTo ?? {}
    },
    potentialRoles() {
      if (this.prospect.groups) {
        return this.prospect.groups.map((group) => group.label)
      }
      return []
    },
    expirationDate() {
      if (this.prospect?.pivot?.expired_at) {
        return moment.utc(this.prospect?.pivot?.expired_at, 'YYYY-MM-DD HH:mm:ss').local().format('M/D/yyyy h:mm a')
      }
      return null
    },
  },
  mounted() {
    setTimeout(() => {
      this.preventClosing = false
    }, this.delayToEnableClosing)
  },
  methods: {
    arrayToSentence,
    clearData() {
      this.reasonForProspectDecline = ''
      this.reasonForProspectDeclineOther = ''
    },
    approveProspect() {
      this.$store.commit('personnel/SET_LAST_PROSPECT_ID_PROMPT', this.prospect.id)
      this.$store.commit('personnel/SET_SELECTED_PROSPECT', this.prospect)
      this.$router.push({
        name: 'personnel-management-prospect-creation',
      })
    },
    openConfirmDeclineProspect() {
      this.$bvModal.show('modalConfirmDeclineProspect')
    },
    openReasonForDeclineProspect() {
      this.clearData()
      this.$bvModal.show('modalReasonForDeclineProspect')
    },
    openApproveProspect() {
      this.$bvModal.show('modalNewProspectPrompt')
    },
    async declineProspect() {
      this.modalButtonLoading = true
      try {
        await declineProspect({
          prospectId: this.prospect.id,
          reasonForDecline: this.reasonForProspectDecline,
          reasonForDeclineOther: this.reasonForProspectDeclineOther,
        })
        this.$root.$bvToast.toast(`Prospect ${this.prospect.name} has been declined.`, {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-success',
        })
        this.$store.commit('personnel/DELETE_PROSPECT', this.prospect.id)
        this.$bvModal.hide('modalNewProspectPrompt')
      } catch (e) {
        this.$root.$bvToast.toast('There was an error declining your prospect', {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
      }
    },
    handleClose() {
      this.$store.commit('personnel/SET_LAST_PROSPECT_ID_PROMPT', this.prospect.id)
    },
  },
}
</script>

<template>
  <div>
    <b-modal
      id="modalNewProspectPrompt"
      :modal-class="['right', 'modal-resource']"
      hide-footer
      :hide-header="!isMobile"
      :hide-header-close="preventClosing"
      :no-close-on-backdrop="preventClosing"
      :no-close-on-esc="preventClosing"
      @hide="handleClose"
      @ok="approveProspect"
      @cancel="openConfirmDeclineProspect">
      <div :class="$style.container">
        <div :class="$style.headerContainer">
          <h2>New Motto Recruit Available</h2>
          <BaseIcon class="color-white" name="user-plus" />
        </div>
        <p v-if="expirationDate" :class="$style.subHeaderContainer"
          >You have until <span class="color-secondary-red">{{ expirationDate }}</span> to respond, or this recruit will
          automatically be declined.</p
        >
        <div :class="$style.infoContainer">
          <div>
            <div :class="$style.infoGroupContainer">
              <div :class="$style.infoGroup">
                <span>Name:</span>
                <p>{{ prospect.name }}</p>
              </div>
              <div v-if="potentialRoles?.length" :class="$style.infoGroup">
                <span>Potential Roles:</span>
                <p>{{ arrayToSentence(potentialRoles, ', ', ', ') }}</p>
              </div>
            </div>
          </div>
        </div>
        <SimpleDivider />
        <p v-if="prospect.recruiterNote" :class="$style.recruiterNotes">
          Hey {{ currentUser.firstName }},<br />
          {{ prospect.recruiterNote }}
        </p>
        <div :class="$style.footer">
          <div :class="$style.recruiterInfo">
            <ProfilePicture :class="$style.profilePicture" :src="assignedTo.photoUrl" />
            <div>
              <p>{{ assignedTo.name }}</p>
              <p v-if="assignedTo.jobTitle">{{ assignedTo.jobTitle }}</p>
              <p>{{ assignedTo.phone }}</p>
              <a :href="`mailto:${assignedTo.email}`">{{ assignedTo.email }}</a>
            </div>
          </div>
          <InfoBlock icon-color="primary"> Click “Accept” to begin the MottoCenter account creation process.</InfoBlock>
          <div :class="$style.buttons">
            <BaseButton theme="white" @click="openConfirmDeclineProspect">Decline</BaseButton>
            <BaseButton theme="blue" @click="approveProspect">Accept</BaseButton>
          </div>
        </div>
      </div>
    </b-modal>
    <b-modal
      id="modalConfirmDeclineProspect"
      modal-class="confirmation-modal"
      title="Decline Prospect"
      ok-variant="danger"
      ok-title="Yes"
      :centered="true"
      :cancel-disabled="modalButtonLoading"
      :ok-disabled="modalButtonLoading"
      @cancel="openApproveProspect"
      @ok="openReasonForDeclineProspect">
      Are you sure you want to decline this Prospect?
    </b-modal>
    <b-modal
      id="modalReasonForDeclineProspect"
      modal-class="confirmation-modal"
      title="Decline Prospect"
      ok-variant="danger"
      ok-title="Submit"
      :centered="true"
      :cancel-disabled="modalButtonLoading"
      :ok-disabled="modalButtonLoading || !isReasonForDeclineValid"
      @cancel="openApproveProspect"
      @ok="declineProspect">
      Please provide your reason for declining this prospect. Your feedback helps us narrow our search to prospects that
      align with your business needs.
      <FormItem
        label="Reason For Prospect Decline"
        edit-mode
        class="mt-4 flex-shrink-0"
        :value.sync="reasonForProspectDecline"
        :edit-select-values="reasonsForProspectDecline"
        :options="{ hideLabel: true }"
        rules="required"
        placeholder="Select..." />
      <FormItem
        v-show="reasonForProspectDecline === 'Other'"
        class="mt-4"
        textarea
        label="Other"
        rules="max:200|required"
        :character-limit-options="{ showOnlyError: true }"
        :value.sync="reasonForProspectDeclineOther"
        :options="{ hideLabel: true }"
        edit-mode />
    </b-modal>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.container {
  @media (max-width: $lg-breakpoint) {
    width: 100vw;
    min-width: 100vw;
    padding: 8px;
  }

  display: flex;
  flex-direction: column;
  max-width: 600px;
  min-height: 100%;
  padding: 10px 20px;
}

.previousButton {
  > svg {
    width: 30px;
    height: 30px;
  }
}

.nextButton {
  > svg {
    width: 30px;
    height: 30px;
    transform: rotate(180deg);
  }
}

.buttons {
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 10px 20px;
  margin-top: 10px;

  > div {
    display: flex;
    align-items: center;
    cursor: pointer;
    gap: 5px;

    p {
      font-size: 16px;
      font-weight: bold;
      color: $secondary-orange;
    }
  }
}

.headerContainer {
  @media (max-width: $lg-breakpoint) {
    width: calc(100% - 40px);
    padding: 8px;
    margin: 0 0 0 18px;
    text-align: center;
  }

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 20px 0;
  margin: 0 0 5px;
  overflow: hidden;
  background-color: $secondary-green;
  border-radius: 8px;

  h2 {
    z-index: 1;
    margin-bottom: 0;
    font-size: 24px;
    font-weight: normal;
    color: $white;
    text-align: center;
  }

  svg {
    margin-left: 20px;
    font-size: 32px;
  }
}

.subHeaderContainer {
  padding: 20px;
  font-size: 16px;
  text-align: center;
}

.descriptionsContainer {
  margin: 15px 50px 0;
  color: $primary-blue;
}

.courseInfoContainer {
  @media (max-width: $lg-breakpoint) {
    flex-direction: column;
  }

  display: flex;
  width: 80%;
  margin: 10px auto 0;
}

.infoContainer {
  padding: 2px 2px 8px;
  background: $white;
  border-radius: 8px;

  h3 {
    @media (max-width: $lg-breakpoint) {
      margin-top: 10px;
      line-height: normal;
    }

    margin: 0 0 0 6px;
    font-size: 18px;
    line-height: 55px;
  }
}

.infoGroupContainer {
  display: flex;
  justify-content: space-between;
  padding: 8px 20px;
  gap: 10px;
}

.infoGroup {
  span {
    display: block;
    text-align: left;
  }

  p {
    font-size: 16px;
    font-weight: bold;
  }
}

.courseTooltip {
  div[class='tooltip-inner'] {
    width: 300px;
    max-width: 270px;
    padding: 2px;
  }
}

.recruiterNotes {
  padding: 0 20px;
  margin-bottom: 30px;
  text-align: center;
}

.recruiterInfo {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
}

.profilePicture {
  margin: 0 20px;
}

.footer {
  margin-top: auto;
}
</style>
